import { normalizeLedgerFields } from "../../lib/ledger-field-compat";
import type { DashboardData } from "../../types";
import type { MooAction, MooMessage } from "../../types/moo";
import { reconciledMonths } from "../../lib/reconciliation";

// Talking to the assistant, and the one check worth doing before its suggestions are accepted:
// whether confirming them would reopen a month whose balances were already agreed.

export function proposalReconciledMonths(data: DashboardData, actions: MooAction[]) {
  const dates: string[] = [];
  for (const action of actions) {
    if (action.kind === "expense_add" && action.expense?.date) dates.push(action.expense.date);
    if (action.kind === "expense_delete" && action.expected?.date) dates.push(action.expected.date);
    if (action.kind === "expense_edit" && action.expected && action.expense) {
      const changed = action.expense.date !== action.expected.date.slice(0, 10) || action.expense.accountId !== action.expected.accountId || Number(action.expense.amount) !== action.expected.amount || (action.expense.toAccountId || "") !== (action.expected.toAccountId || "");
      if (changed) dates.push(action.expected.date, action.expense.date);
    }
  }
  return reconciledMonths(data, dates);
}

class MooConnectionError extends Error {}
class MooResponseError extends Error {
  constructor(message: string, public status: number) { super(message); }
}

function readEvent(line: string, progress: (s: string) => void): { messages: MooMessage[] } | undefined {
  const event = normalizeLedgerFields(JSON.parse(line)) as { progress?: string; error?: string; status?: number; result?: { messages: MooMessage[] } };
  if (event.progress) progress(event.progress);
  if (event.error) throw new MooResponseError(event.error, event.status || 400);
  return event.result;
}

async function sendAttempt(body: string, progress: (s: string) => void, signal: AbortSignal) {
  const response = await fetch("/api/moo/send", {
    method: "POST",
    headers: { "Content-Type": "application/json", "X-Barnyard-Fields": "title" },
    body, signal,
  });
  if (!response.ok) {
    const value = (await response.json().catch(() => ({}))) as { error?: string };
    if ([502, 503, 504].includes(response.status)) throw new MooConnectionError("The connection to Moo was interrupted.");
    throw new MooResponseError(value.error || "Moo could not accept this message.", response.status);
  }
  if (!response.body) throw new MooConnectionError("Moo returned an empty response.");
  const reader = response.body.getReader(), decoder = new TextDecoder();
  let buffer = "";
  try {
    while (true) {
      const { value, done } = await reader.read();
      buffer += decoder.decode(value, { stream: !done });
      const lines = buffer.split("\n");
      buffer = lines.pop() || "";
      if (done && buffer.trim()) lines.push(buffer);
      for (const line of lines.filter(line => line.trim())) {
        const result = readEvent(line, progress);
        if (result) return result;
      }
      if (done) throw new MooConnectionError("The reply connection ended early.");
    }
  } finally {
    await reader.cancel().catch(() => {});
    reader.releaseLock();
  }
}

/** Retries only this idempotent send, never a proposal confirmation or ledger write. */
export async function sendMoo(body: unknown, progress: (s: string) => void): Promise<{ messages: MooMessage[] }> {
  const serialized = JSON.stringify(body), signal = AbortSignal.timeout(300000);
  const identity = body as { chatId: string; requestId: string };
  let failures = 0;
  let recovering = false;
  while (true) {
    try {
      if (recovering) {
        const response = await fetch(`/api/moo/messages?chatId=${encodeURIComponent(identity.chatId)}`, {
          headers: { "X-Barnyard-Fields": "title" }, signal,
        });
        if (response.ok) {
          const state = normalizeLedgerFields(await response.json()) as { messages: MooMessage[]; working?: boolean };
          if (state.working) {
            progress("Moo is still working…");
            await new Promise(resolve => setTimeout(resolve, 2000));
            continue;
          }
          const sent = state.messages.findIndex(message => message.id === identity.requestId);
          if (sent >= 0) {
            if (state.messages.slice(sent + 1).some(message => message.role === "assistant")) return { messages: state.messages };
            throw new MooResponseError("Your message reached Moo, but its reply is incomplete. Reopen this conversation to check progress.", 409);
          }
        } else if (response.status !== 404) {
          if ([401, 403].includes(response.status)) throw new MooResponseError("Please sign in again to continue this conversation.", response.status);
          throw new MooConnectionError("The connection to Moo was interrupted.");
        }
        recovering = false;
      }
      return await sendAttempt(serialized, progress, signal);
    } catch (error) {
      if (signal.aborted) throw new Error("Moo took too long to answer. Reopen this conversation to check its result.");
      // A reconnect can meet the original request's conversation lock. Wait for it to finish.
      const working = failures > 0 && error instanceof MooResponseError && error.status === 409 && error.message.startsWith("Moo is already working");
      const interrupted = error instanceof MooConnectionError || error instanceof TypeError || error instanceof SyntaxError;
      if (!working && (!interrupted || ++failures >= 3)) {
        if (interrupted) throw new Error("Moo couldn’t reconnect. Please retry this message shortly.");
        throw error;
      }
      recovering = true;
      progress(working ? "Moo is still working…" : "Reconnecting to Moo…");
      await new Promise(resolve => setTimeout(resolve, working ? 2000 : 1000));
    }
  }
}
